import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { CopyButton } from '@/_shared/components/CopyButton';
import { Button, ButtonGroup } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/_shared/components/ui/sheet';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useSyntaxStyle } from '@/_shared/hooks/useSyntaxStyle';
import { codeBlockCustomStyle, SyntaxHighlighter } from '@/_shared/lib/syntax';

import { AccountCopyWarning } from '../accountCopies/AccountCopyWarning';
import { refreshPermissionOnRefusal, useWarehousePermission } from './accountCreatePermission';
import type { FailedStep } from './addAccount/PlatformAccountStep';
import { PermissionNotice } from './PermissionNotice';
import { refusalMessage } from './refusalCopy';
import { DownloadButton } from './shared/DownloadButton';
import { FailedStepCallout } from './shared/FailedStepCallout';
import { OutputSection } from './shared/OutputSection';
import type { WarehouseAccount } from './useWarehouseAccounts';

const slotColumn = (slot: 1 | 2): string => (slot === 1 ? 'RSA_PUBLIC_KEY' : 'RSA_PUBLIC_KEY_2');

export function RotateKeyDrawer({
  account,
  onOpenChange,
}: {
  account: WarehouseAccount | null;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const syntaxStyle = useSyntaxStyle();
  // A refused step refreshes the shared permission; the open drawer follows it.
  const permission = useWarehousePermission();
  const blocked = permission.blocked !== null;
  const [failedStep, setFailedStep] = React.useState<FailedStep | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [replaced, setReplaced] = React.useState(false);
  const start = useMutation(
    orpc.settings.warehouses.startKeyRotation.mutationOptions({
      onError: (err) => refreshPermissionOnRefusal(queryClient, orpc, err),
    }),
  );
  const verify = useMutation(
    orpc.settings.warehouses.verifyKeyRotation.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries({ queryKey: orpc.settings.warehouses.key() }),
      onError: (err) => {
        refreshPermissionOnRefusal(queryClient, orpc, err);
        const e = err as { data?: { failedStep?: FailedStep; reason?: string } };
        if (e.data?.failedStep) return void setFailedStep(e.data.failedStep);
        // Decision 8: another admin's start superseded this rotation, or it expired. Never restart on
        // our own — that would supersede theirs in turn. Say so and let the admin choose.
        if (e.data?.reason === 'setup_expired' || e.data?.reason === 'setup_not_found') return void setReplaced(true);
        setFormError(refusalMessage(err, 'Verification failed'));
      },
    }),
  );
  // After `connection_changed`: RudderStack already holds the new key, so the switch is finished
  // without a new rotation (whose SQL could overwrite the key this connection still signs with).
  const finish = useMutation(
    orpc.settings.warehouses.finishKeyConversion.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries({ queryKey: orpc.settings.warehouses.key() }),
      onError: (err) => {
        refreshPermissionOnRefusal(queryClient, orpc, err);
        setFormError(refusalMessage(err, 'Could not finish switching to the new key'));
      },
    }),
  );
  const begin = (currentKeySlot?: 1 | 2) => {
    if (!account) return;
    setFailedStep(null);
    setFormError(null);
    setReplaced(false);
    verify.reset();
    finish.reset();
    start.mutate({ connectionId: account.id, ...(currentKeySlot ? { currentKeySlot } : {}) });
  };
  // Start, or resume this admin's own pending rotation (server-side E12 → E10), once per opened account.
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- starting the rotation is a call to RudderStack (an external system); the resets clear the previous account's drawer state first
    begin();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per opened account
  }, [account?.id]);

  const rotation = start.data?.rotation;
  const done = verify.data ?? finish.data;
  const startRefusal = (start.error as { data?: { reason?: string } } | null)?.data?.reason;
  // Decision 10 (LC1): refused before any config-backend call; there is no SQL to show.
  const sharedUser = startRefusal === 'shared_snowflake_user';
  const conversionIncomplete = startRefusal === 'conversion_incomplete';
  return (
    <Sheet open={!!account} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-drawer">
        <SheetHeader className="border-b border-border p-4">
          <SheetTitle>Rotate key — {account?.name}</SheetTitle>
        </SheetHeader>
        <div className="grid flex-1 gap-5 overflow-y-auto p-4">
          {/* ACT2-1124: a copy of another workspace's account shares its login with the original. */}
          <AccountCopyWarning accountId={account?.rsAccountId} />
          {start.isError && sharedUser && (
            <Callout variant="warning">
              This Snowflake user is used by another connection; give each connection its own user first.
            </Callout>
          )}
          {start.isError && conversionIncomplete && !done && (
            <Callout variant="warning">
              <p>
                RudderStack already holds this connection’s new key, but the connection still signs in with the
                workspace key. Finish switching it to the new key; no SQL is needed.
              </p>
              <div className="mt-2">
                <Button
                  type="button"
                  variant="primary"
                  label="Finish switching to the new key"
                  loading={finish.isPending}
                  disabled={blocked}
                  onClick={() => {
                    setFormError(null);
                    finish.mutate({ connectionId: account!.id });
                  }}
                />
              </div>
            </Callout>
          )}
          {start.isError && !sharedUser && !conversionIncomplete && (
            <Callout variant="error">{refusalMessage(start.error, 'Could not start the rotation')}</Callout>
          )}
          {start.data?.converting && !done && (
            <Callout variant="info">
              This connection signs in with the workspace key shared by your legacy Snowflake connections. Rotating
              converts this connection to its own key, held by RudderStack.
            </Callout>
          )}
          {!done && <PermissionNotice permission={permission} />}
          {failedStep && <FailedStepCallout step={failedStep} />}
          {formError && <Callout variant="error">{formError}</Callout>}
          {replaced && (
            <Callout variant="warning">
              <p>This rotation was replaced by another admin, or it expired. The current key still works.</p>
              <Button
                type="button"
                variant="secondary"
                label="Start a new rotation"
                disabled={blocked}
                onClick={() => begin()}
              />
            </Callout>
          )}
          {rotation && !done && !replaced && (
            <>
              <Callout variant="highlight">
                Run this SQL in Snowflake, then click Verify. The current key keeps working until verification succeeds.
                Valid until {new Date(rotation.expiresAt).toLocaleString()}.
              </Callout>
              <OutputSection
                label="Rotate SQL"
                action={
                  <ButtonGroup>
                    <DownloadButton text={rotation.rotateSql} filename="rudderstack-snowflake-rotate.sql" />
                    <CopyButton text={rotation.rotateSql} label="Copy" copyLabel="Rotate SQL" />
                  </ButtonGroup>
                }
              >
                <SyntaxHighlighter language="sql" style={syntaxStyle} customStyle={codeBlockCustomStyle}>
                  {rotation.rotateSql}
                </SyntaxHighlighter>
              </OutputSection>
              <OutputSection
                label="New public key"
                action={<CopyButton text={rotation.publicKey.pem} label="Copy" copyLabel="Public Key" />}
              >
                <pre className="font-mono text-xs whitespace-pre text-grey-800">{rotation.publicKey.pem}</pre>
              </OutputSection>
              <OutputSection label="Fingerprint">
                <code className="block font-mono text-xs break-all">{rotation.publicKey.fingerprint}</code>
              </OutputSection>
              <Button
                type="button"
                variant="primary"
                label="Verify"
                loading={verify.isPending}
                disabled={blocked}
                onClick={() => {
                  setFailedStep(null);
                  setFormError(null);
                  verify.mutate({ connectionId: account!.id, rotationId: rotation.id });
                }}
              />
              {/* Merge call 5: the rotate SQL's Step 1 tells the admin to restart naming the slot that holds the live key. */}
              <Button
                type="button"
                variant="tertiary"
                label={`The current key is in ${slotColumn(rotation.keySlot)}: restart`}
                disabled={blocked}
                onClick={() => begin(rotation.keySlot)}
              />
            </>
          )}
          {done && (
            <>
              <Callout variant="success">
                Key rotated. RudderStack and Lookout now use {done.key.fingerprint}.
                {done.converted ? ' This connection now has its own key.' : ''}
                {done.workspaceKeyDeleted
                  ? ' No connection used the workspace key any more, so the workspace key was deleted.'
                  : ''}
              </Callout>
              {done.teardownSql ? (
                <OutputSection
                  label="Remove the old key (optional, recommended)"
                  action={<CopyButton text={done.teardownSql} label="Copy" copyLabel="Teardown SQL" />}
                >
                  <SyntaxHighlighter language="sql" style={syntaxStyle} customStyle={codeBlockCustomStyle}>
                    {done.teardownSql}
                  </SyntaxHighlighter>
                </OutputSection>
              ) : done.teardownBlockedByKeyChange ? (
                <Callout variant="warning">
                  The key RudderStack holds changed while this connection converted, so the old key is kept. Rotate
                  again from this card; the next rotation repairs it.
                </Callout>
              ) : done.teardownBlockedBySharedUser ? (
                <Callout variant="info">
                  The old key stays on the Snowflake user because another connection signs in as this user. Give each
                  connection its own Snowflake user, then remove the old key.
                </Callout>
              ) : null}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
